import { useState, useEffect } from 'react';
import { Editor } from '@monaco-editor/react';
import { useTheme } from '../contexts/ThemeContext';

const TextEditor = ({ 
    content, 
    readOnly = false, 
    className = '', 
    onChange, 
    language,
}) => {
    const [editorValue, setEditorValue] = useState('');
    const { isDark } = useTheme();
    const [isContentLoaded, setIsContentLoaded] = useState(false);
    const [editor, setEditor] = useState(null);

    useEffect(() => {
        if (content !== undefined) {
            setEditorValue(content);
            setIsContentLoaded(true);
        }
    }, [content]);

    const handleEditorChange = (value) => {
        if (!readOnly) {
            setEditorValue(value || '');
            // Call the onChange callback if provided
            if (onChange) {
                onChange(value || '');
            }
        }
    };

    const handleEditorDidMount = (editorInstance, monaco) => {
        // Store editor reference for theme updates
        setEditor(editorInstance);
        // Ensure the theme is set correctly when the editor mounts
        const theme = isDark ? 'vs-dark' : 'vs-light';
        monaco.editor.setTheme(theme);
    };

    // Handle theme changes after editor is mounted
    useEffect(() => {
        if (editor) {
            const theme = isDark ? 'vs-dark' : 'vs-light';
            editor.updateOptions({ theme });
        }
    }, [isDark, editor]);

    // Show loading state while content is being loaded
    if (!isContentLoaded) {
        return (
            <div className={`${className} min-h-[400px] flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
                <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading...</div>
            </div>
        );
    }

    return (
        <div className={`${className} min-h-0 h-full flex flex-col`}>
            <Editor
                height="100%"
                language={language || 'text'}
                value={editorValue}
                onChange={handleEditorChange}
                onMount={handleEditorDidMount}
                options={{
                    readOnly: readOnly,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 14,
                    lineNumbers: 'on',
                    wordWrap: 'on',
                    automaticLayout: true,
                    theme: isDark ? 'vs-dark' : 'vs-light',
                    selectOnLineNumbers: true,
                    roundedSelection: false,
                    cursorStyle: 'line',
                    glyphMargin: false,
                    folding: true,
                    lineDecorationsWidth: 0,
                    lineNumbersMinChars: 3,
                    renderLineHighlight: 'line',
                    scrollbar: {
                        vertical: 'auto',
                        horizontal: 'auto',
                        useShadows: false,
                        verticalHasArrows: false,
                        horizontalHasArrows: false,
                        verticalScrollbarSize: 8,
                        horizontalScrollbarSize: 8,
                    },
                }}
            />
        </div>
    );
};

export default TextEditor;
